import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
  type Locale,
  format as formatDateFns,
  formatDistanceToNow as formatDistanceToNowDateFns,
  parse as parseDateFns,
} from 'date-fns';
import { de, enUS, nl } from 'date-fns/locale';

const getLocale = (language: string): Locale => {
  switch (language) {
    case 'en':
      return enUS;
    case 'de':
      return de;
    case 'nl':
      return nl;
    default:
      return enUS;
  }
};

export const useLocaleDateFns = () => {
  const { i18n } = useTranslation();

  const locale = useMemo(() => getLocale(i18n.language), [i18n.language]);

  const format: typeof formatDateFns = useCallback(
    (date, fmt, options) =>
      formatDateFns(date, fmt, {
        locale,
        ...options,
      }),
    [locale],
  );

  const formatDistanceToNow: typeof formatDistanceToNowDateFns = useCallback(
    (date, options) =>
      formatDistanceToNowDateFns(date, {
        locale,
        ...options,
      }),
    [locale],
  );

  const parse: typeof parseDateFns = useCallback(
    (dateString, formatString, referenceDate, options) =>
      parseDateFns(dateString, formatString, referenceDate, {
        locale,
        ...options,
      }),
    [locale],
  );

  return {
    locale,
    format,
    formatDistanceToNow,
    parse,
  };
};
